import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { GLOBAL_UI } from '@/config/missionContent';

const LEON_IMG = 'https://static.prod-images.emergentagent.com/jobs/febf00d3-02f1-4680-9d85-457affdefbb8/images/fac4cec9781d55a4a9535dcdac48735d36c85cc5beef980c8cf08eac42e7645b.png';

export default function LeonDialogue({ message, onDismiss, autoAdvance = false, delay = 0 }) {
  const [displayedText, setDisplayedText] = useState('');
  const [typing, setTyping] = useState(true);
  const [visible, setVisible] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  useEffect(() => {
    if (!visible) return;
    setDisplayedText('');
    setTyping(true);
    let i = 0;
    intervalRef.current = setInterval(() => {
      if (i < message.length) {
        setDisplayedText(message.slice(0, i + 1));
        i++;
      } else {
        setTyping(false);
        clearInterval(intervalRef.current);
        if (autoAdvance && onDismiss) {
          setTimeout(onDismiss, 2000);
        }
      }
    }, 25);
    return () => clearInterval(intervalRef.current);
  }, [message, visible, autoAdvance, onDismiss]);

  const handleClick = () => {
    if (typing) {
      clearInterval(intervalRef.current);
      setDisplayedText(message);
      setTyping(false);
    } else if (onDismiss) {
      onDismiss();
    }
  };

  if (!visible) return null;

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      className="leon-dialogue"
      onClick={handleClick}
      data-testid="leon-dialogue"
      role="button"
      tabIndex={0}
    >
      <div className="leon-portrait">
        <img src={LEON_IMG} alt="Leon S. Kennedy" />
        <div className="absolute bottom-0 left-0 right-0 bg-black/90 text-center" style={{ padding: '1px 0' }}>
          <span className="font-terminal text-xs tracking-widest text-green-400 uppercase">{GLOBAL_UI.leon.shortName}</span>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="leon-name">{GLOBAL_UI.leon.name}</div>
        <div className="leon-text">
          {displayedText}
          {typing && <span className="typewriter-cursor" />}
        </div>
        {!typing && (
          <div className="text-xs text-green-800 mt-2 font-terminal tracking-wider animate-pulse">
            {GLOBAL_UI.leon.clickToContinue}
          </div>
        )}
      </div>
      {!typing && onDismiss && (
        <button onClick={(e) => { e.stopPropagation(); onDismiss(); }} className="text-gray-600 hover:text-green-400 transition-colors" data-testid="leon-dismiss">
          <X size={16} />
        </button>
      )}
    </motion.div>
  );
}
